import type { Locale } from '@/shared/i18n/locale'
import {
  DEFAULT_CLINIC_DATE_FORMAT,
  DEFAULT_CLINIC_TIME_FORMAT,
  formatClinicFullCivilDay,
  formatClinicDate,
  formatClinicTime,
} from '@/shared/i18n/clinicFormats'
import type { ClinicDateFormat, ClinicTimeFormat } from '@/shared/session/types'

/**
 * Civil-day arithmetic and formatting.
 *
 * The rule this module exists to enforce: a schedule day is a civil day in the
 * CLINIC's zone, never the viewer's.
 *
 * The client no longer decides which day is today — the contract makes `date`
 * optional and the service resolves it in the zone it owns. What remains here
 * is arithmetic on a date the server already resolved, and formatting: a civil
 * date formatted without a zone, an instant formatted in the clinic's.
 */

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/

export function isDayIso(value: unknown): value is string {
  return parseCivilDate(value) !== null
}

/**
 * Move a civil date by whole days. Pure calendar arithmetic at UTC noon, with
 * no zone involved: the previous day of a date is the previous calendar date
 * regardless of any daylight-saving transition in between.
 */
export function shiftDay(dayIso: string, deltaDays: number): string {
  const parsed = parseCivilDate(dayIso)
  if (!parsed) {
    return ''
  }
  const [year, month, day] = parsed
  const moved = new Date(Date.UTC(year!, month! - 1, day! + deltaDays, 12))
  return `${moved.getUTCFullYear()}-${pad(moved.getUTCMonth() + 1)}-${pad(moved.getUTCDate())}`
}

/** A civil date has no time zone, so it is formatted in UTC to avoid drift. */
export function formatDayHeading(dayIso: string, locale: Locale): string {
  return formatClinicFullCivilDay(dayIso, locale)
}

/** Same civil-date rule as the heading, but compact enough for a row. */
export function formatCivilDate(
  dayIso: string | null,
  locale: Locale,
  dateFormat: ClinicDateFormat = DEFAULT_CLINIC_DATE_FORMAT,
): string | null {
  const parsed = parseCivilDate(dayIso)
  if (!parsed) {
    return null
  }
  const [year, month, day] = parsed
  return formatClinicDate(new Date(Date.UTC(year, month - 1, day, 12)), locale, dateFormat, 'UTC')
}

/**
 * Age is owned by the clinic day being shown, not by the workstation clock.
 * Both inputs are civil dates, so no instant or viewer zone participates.
 *
 * EVERY DERIVED AGE IN `web/` COMES THROUGH HERE, including the patient
 * surfaces': `patientAgeYears` delegates to it against the day
 * `useClinicToday` resolves. The schedule passes the day it is showing, so a
 * clinician reading Thursday's list sees the age each patient will be on
 * Thursday; a chart passes today. One rule, two days, no second implementation
 * that can drift from this one.
 */
export function ageAtCivilDay(birthDateIso: string | null, dayIso: string): number | null {
  const birth = parseCivilDate(birthDateIso)
  const day = parseCivilDate(dayIso)
  if (!birth || !day) {
    return null
  }
  const [birthYear, birthMonth, birthDay] = birth
  const [dayYear, dayMonth, dayDay] = day
  let age = dayYear - birthYear
  if (dayMonth < birthMonth || (dayMonth === birthMonth && dayDay < birthDay)) {
    age -= 1
  }
  return age >= 0 ? age : null
}

/**
 * An event time IS an instant, so it is formatted in the clinic's zone. Using
 * the viewer's zone here would show a Jerusalem 09:00 appointment as 06:00 to a
 * UTC workstation.
 */
export function formatEventTime(
  instant: string,
  locale: Locale,
  timeZone: string,
  timeFormat: ClinicTimeFormat = DEFAULT_CLINIC_TIME_FORMAT,
): string {
  const date = new Date(instant)
  if (Number.isNaN(date.getTime())) {
    return ''
  }
  return formatClinicTime(date, locale, timeFormat, timeZone)
}

function pad(value: number): string {
  return String(value).padStart(2, '0')
}

function parseCivilDate(value: unknown): [number, number, number] | null {
  if (typeof value !== 'string' || !ISO_DAY.test(value)) {
    return null
  }
  const [year, month, day] = value.split('-').map(Number)
  const probe = new Date(Date.UTC(year!, month! - 1, day!))
  // Rejects 2026-02-30 and friends, which match the pattern but do not exist.
  if (probe.getUTCFullYear() !== year || probe.getUTCMonth() !== month! - 1 || probe.getUTCDate() !== day) {
    return null
  }
  return [year!, month!, day!]
}

/** The search the Schedule route accepts. Anything else is dropped. */
export interface ScheduleSearch {
  date?: string
  eventId?: string
}

/**
 * Validate the Schedule route's search, for the ROUTE and the PAGE alike.
 *
 * A non-strict `useSearch` returns the location's RAW search: it never passes
 * through the route's `validateSearch`, so a page that casts the result is
 * trusting a shape nothing checked. The two callers run this same function
 * instead, which is what makes the day the surface renders — and the day a
 * Patient link carries back — the day the route actually accepted.
 *
 * A malformed date survives this deliberately. The surface NAMES an invalid day
 * rather than silently substituting one, and it cannot name what the validator
 * already threw away; `isDayIso` is the check that decides whether the day is
 * rendered or refused.
 */
export function validateScheduleSearch(search: Record<string, unknown>): ScheduleSearch {
  const raw = search['date']
  const eventId = search['eventId']
  return {
    ...(typeof raw === 'string' ? { date: raw } : {}),
    ...(typeof eventId === 'string' &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(eventId)
      ? { eventId }
      : {}),
  }
}
